import { useEffect, useState } from "react";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { getConsent, setConsent } from "@/lib/cookieConsent";

const HIDDEN_PREFIXES = ["/admin", "/angajati"];

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const path = window.location.pathname;
    if (HIDDEN_PREFIXES.some((p) => path.startsWith(p))) return;
    const existing = getConsent();
    if (!existing) {
      setVisible(true);
    } else {
      setAnalytics(!!existing.analytics);
      setMarketing(!!existing.marketing);
    }
    const openHandler = () => {
      const c = getConsent();
      setAnalytics(!!c?.analytics);
      setMarketing(!!c?.marketing);
      setSettingsOpen(true);
    };
    window.addEventListener("open-cookie-settings", openHandler);
    return () => window.removeEventListener("open-cookie-settings", openHandler);
  }, []);

  const acceptAll = () => {
    setConsent({ analytics: true, marketing: true });
    setVisible(false);
    setSettingsOpen(false);
  };
  const rejectAll = () => {
    setConsent({ analytics: false, marketing: false });
    setVisible(false);
    setSettingsOpen(false);
  };
  const saveSettings = () => {
    setConsent({ analytics, marketing });
    setVisible(false);
    setSettingsOpen(false);
  };

  const Category = ({ title, desc, checked, onChange, disabled, obligatoriu, testId }) => (
    <div className="py-5 border-b border-slate-100 last:border-0">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-bold text-sm uppercase tracking-wide text-[#0F1E36]">{title}</p>
          {obligatoriu && (
            <span className="text-[10px] font-bold uppercase tracking-wider bg-[#0F1E36] text-white px-2 py-0.5 rounded">
              Obligatorii
            </span>
          )}
        </div>
        <Switch
          checked={checked}
          onCheckedChange={onChange}
          disabled={disabled}
          data-testid={testId}
          className="data-[state=checked]:bg-[#FF5A1F] shrink-0"
        />
      </div>
      <p className="text-sm text-slate-500 mt-2 pr-10">{desc}</p>
    </div>
  );

  return (
    <>
      {visible && (
        <div
          data-testid="cookie-consent-banner"
          className="fixed bottom-0 inset-x-0 z-[60] p-3 sm:p-4"
        >
          <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl border border-[#E2E8F0] p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <span className="w-10 h-10 rounded-xl bg-[#FFF1EC] flex items-center justify-center shrink-0">
                <Cookie className="w-5 h-5 text-[#FF5A1F]" />
              </span>
              <div>
                <p className="font-display font-bold text-[#0F1E36]">Acest site folosește cookie-uri</p>
                <p className="text-sm text-slate-500 mt-1">
                  Folosim cookie-uri pentru a îmbunătăți experiența ta, a analiza traficul și a
                  personaliza conținutul. Poți accepta toate, respinge toate sau alege ce categorii activezi.
                  <span className="block text-xs text-slate-400 mt-1">
                    Conform GDPR și Directivei ePrivacy. Îți poți retrage consimțământul oricând din butonul „Setări cookie-uri" din subsol.
                  </span>
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="cookie-settings-button" className="border-[#E2E8F0] font-semibold">
                Setări
              </Button>
              <Button variant="outline" onClick={rejectAll} data-testid="cookie-reject-all-button" className="border-[#E2E8F0] font-semibold">
                Respinge toate
              </Button>
              <Button onClick={acceptAll} data-testid="cookie-accept-all-button" className="bg-[#FF5A1F] hover:bg-[#E04B14] text-white font-semibold">
                Acceptă toate
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cookie-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Setări cookie-uri</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">
            Folosim cookie-uri pentru a îmbunătăți experiența ta, a analiza traficul și a
            personaliza conținutul. Poți accepta toate, respinge toate sau alege ce categorii activezi.
          </p>
          <div className="mt-1">
            <Category
              title="Strict necesare"
              obligatoriu
              disabled
              checked
              desc="Cookie-urile esențiale pentru funcționarea site-ului (sesiune, securitate, preferință de limbă). Nu pot fi dezactivate."
              testId="cookie-cat-necessary"
            />
            <Category
              title="Statistică & performanță"
              desc="Ne ajută să înțelegem câți vizitatori avem și cum este folosit site-ul. Datele sunt agregate și anonime."
              checked={analytics}
              onChange={setAnalytics}
              testId="cookie-cat-analytics"
            />
            <Category
              title="Marketing & remarketing"
              desc="Momentan neutilizat. În viitor ar putea permite afișarea de reclame relevante pe alte site-uri (ex. Facebook Pixel, Google Ads). Necesită consimțământul tău explicit."
              checked={marketing}
              onChange={setMarketing}
              testId="cookie-cat-marketing"
            />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-between">
            <Button onClick={saveSettings} data-testid="cookie-save-settings-button" className="bg-[#FF5A1F] hover:bg-[#E04B14] text-white font-semibold">
              Salvează preferințele
            </Button>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={rejectAll} className="border-[#E2E8F0] font-semibold">Respinge toate</Button>
              <Button onClick={acceptAll} className="bg-[#0F1E36] hover:bg-[#1A2D4D] text-white font-semibold">Acceptă toate</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
